import React from "react";
import styled from "@emotion/styled/macro";
import {
  Banner,
  BannerTypes,
  CompareNames,
  tokens,
  useSingletonMobileMediaQuery,
} from "@sunrun/experience-ui-components";
import { NAME_MISMATCH_BANNER_MESSAGE } from "./constants";
import { StepBody, StepHeader } from "./SharedStyles";

type NameComparisonStepProps = {
  title: string;
  description: string;
  leftLabel: string;
  leftNames: string[];
  rightLabel: string;
  rightNames: string[];
  onUpdate: () => void;
  mismatchBanner?: {
    title: string;
    bannerTestId: string;
  };
  missingBanner?: {
    title: string;
    bannerTestId: string;
    actionLabel: string;
    onAction: () => void;
    actionTestId: string;
  };
  titleEmptyContent?: React.ReactNode;
  contractReviewBanners?: React.ReactNode;
};

const NameComparisonStep: React.FC<NameComparisonStepProps> = ({
  title,
  description,
  leftLabel,
  leftNames,
  rightLabel,
  rightNames,
  onUpdate,
  mismatchBanner,
  missingBanner,
  titleEmptyContent,
  contractReviewBanners,
}) => {
  const isMobile = useSingletonMobileMediaQuery();

  return (
    <StepBody isMobile={isMobile}>
      <StepHeader title={title} description={description} onUpdate={onUpdate} />

      {missingBanner && (
        <Banner
          show
          showClose={false}
          bannerType={BannerTypes.ErrorLight}
          bannerTestId={missingBanner.bannerTestId}
          title={missingBanner.title}
          primaryAction={{
            title: missingBanner.actionLabel,
            action: missingBanner.onAction,
            testId: missingBanner.actionTestId,
          }}
        />
      )}

      {mismatchBanner && (
        <Banner
          show
          showClose={false}
          bannerType={BannerTypes.ErrorLight}
          bannerTestId={mismatchBanner.bannerTestId}
          title={mismatchBanner.title}
        >
          {NAME_MISMATCH_BANNER_MESSAGE}
        </Banner>
      )}

      {contractReviewBanners}

      <CompareNames
        textSize={isMobile ? tokens.FONT_SIZE_5 : tokens.FONT_SIZE_6}
        signingNames={leftNames}
        titleNames={rightNames}
        leftTitle={leftLabel}
        rightTitle={rightLabel}
        leftBackground={tokens.BRAND_OFF_GREY}
        rightBackground={tokens.CHARGEBLUE_80}
        rightEmptyContent={titleEmptyContent}
      />
    </StepBody>
  );
};

const NameOnBillNotFound: React.FC<{ billUrl?: string }> = ({ billUrl }) => (
  <NameNotFoundText data-testid="confirm-names-name-on-bill-not-found">
    Name on bill not found, please{" "}
    {billUrl ? (
      <ViewBillLink
        href={billUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="confirm-names-view-bill-link"
      >
        view bill
      </ViewBillLink>
    ) : (
      <ViewBillLinkDisabled
        aria-disabled="true"
        data-testid="confirm-names-view-bill-link-disabled"
      >
        view bill
      </ViewBillLinkDisabled>
    )}{" "}
    and confirm the name matches.
  </NameNotFoundText>
);

const NameNotFoundText = styled.div`
  font-family: Roobert, sans-serif;
  font-size: ${tokens.FONT_SIZE_1}px;
  font-weight: 400;
  text-align: center;
  color: ${tokens.TINTS_OFF_WHITE_20};
  max-width: 100%;
  word-wrap: break-word;
  padding: 20px 0;
`;

const ViewBillLink = styled.a`
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
`;

const ViewBillLinkDisabled = styled.span`
  text-decoration: underline;
  opacity: 0.5;
  cursor: default;
`;

export { NameComparisonStep, NameOnBillNotFound };
export type { NameComparisonStepProps };
